import type {
  CommandResult,
  ProductionFloorBinsResponse,
  ProductionOrderDetail,
  ProductionOrderListResponse,
  ProductionRequestListResponse,
  ProductionTaskListResponse,
  ProductionTraceability,
} from '@warehouse/shared';

import { api } from '../../lib/api-client';
import { listQueryKey, listQueryParams, type ListState } from '../../lib/list-query';

/**
 * The production module's half of the wire.
 *
 * Every mutation posts a CommandBus command and gets a `CommandResult` back
 * (`verdict`, `replayed`, `healing`, `result`) — the same envelope the Android
 * outbox receives from `/api/sync/commands`. `unwrap` peels off the payload for
 * callers that only care about the outcome; `verdict`/`healing` stay reachable
 * for the ones that do.
 */

const BASE = '/production';

export const productionKeys = {
  all: ['production'] as const,
  orders: (state: ListState) => ['production', 'orders', listQueryKey(state)] as const,
  order: (id: string) => ['production', 'order', id] as const,
  traceability: (id: string) => ['production', 'traceability', id] as const,
  tasks: (state: ListState) => ['production', 'tasks', listQueryKey(state)] as const,
  requests: (state: ListState) => ['production', 'requests', listQueryKey(state)] as const,
  floorBins: (areaId?: string | null) => ['production', 'floor-bins', areaId ?? null] as const,
};

// --- reads -----------------------------------------------------------------

export function fetchOrders(state: ListState): Promise<ProductionOrderListResponse> {
  return api.get<ProductionOrderListResponse>(`${BASE}/orders`, { query: listQueryParams(state) });
}

export function fetchOrder(id: string): Promise<ProductionOrderDetail> {
  return api.get<ProductionOrderDetail>(`${BASE}/orders/${id}`);
}

export function fetchTraceability(id: string): Promise<ProductionTraceability> {
  return api.get<ProductionTraceability>(`${BASE}/orders/${id}/traceability`);
}

export function fetchTasks(state: ListState): Promise<ProductionTaskListResponse> {
  return api.get<ProductionTaskListResponse>(`${BASE}/tasks`, { query: listQueryParams(state) });
}

export function fetchRequests(state: ListState): Promise<ProductionRequestListResponse> {
  return api.get<ProductionRequestListResponse>(`${BASE}/requests`, { query: listQueryParams(state) });
}

export function fetchFloorBins(areaId?: string | null): Promise<ProductionFloorBinsResponse> {
  return api.get<ProductionFloorBinsResponse>(`${BASE}/floor-bins`, {
    query: areaId ? { areaId } : undefined,
  });
}

// --- writes ----------------------------------------------------------------

/** Peel the handler payload out of the command envelope. */
export function unwrap<T>(result: CommandResult<T>): T {
  return result.result as T;
}

export interface CreateOrderInput {
  outputArticleId: string;
  qtyBase: string;
  orderNo?: string | undefined;
  plannedStart?: string | null | undefined;
  floorAreaId?: string | null | undefined;
  note?: string | null | undefined;
  components?: { articleId: string; qtyBase: string }[] | undefined;
}

export function createOrder(input: CreateOrderInput): Promise<CommandResult<{ id: string; orderNo: string }>> {
  return api.post(`${BASE}/orders`, input);
}

export function updateOrder(
  id: string,
  input: Partial<Pick<CreateOrderInput, 'qtyBase' | 'plannedStart' | 'floorAreaId' | 'note'>>,
): Promise<CommandResult<{ id: string }>> {
  return api.patch(`${BASE}/orders/${id}`, input);
}

export function setComponents(
  id: string,
  components: { articleId: string; qtyBase: string }[],
): Promise<CommandResult<{ id: string; count: number }>> {
  return api.put(`${BASE}/orders/${id}/components`, { components });
}

export function releaseOrder(id: string): Promise<CommandResult> {
  return api.post(`${BASE}/orders/${id}/release`, {});
}

export function startOrder(id: string): Promise<CommandResult> {
  return api.post(`${BASE}/orders/${id}/start`, {});
}

export function cancelOrder(id: string, reason?: string): Promise<CommandResult> {
  return api.post(`${BASE}/orders/${id}/cancel`, reason ? { reason } : {});
}

export function planStaging(id: string): Promise<CommandResult<{ created: number; taskIds: string[] }>> {
  return api.post(`${BASE}/orders/${id}/staging`, {});
}

export interface StageComponentInput {
  qtyBase: string;
  fromBinId?: string | null | undefined;
  toBinId?: string | null | undefined;
  taskId?: string | null | undefined;
}

export function stageComponent(
  orderId: string,
  componentId: string,
  input: StageComponentInput,
): Promise<CommandResult<{ movedQtyBase: string }>> {
  return api.post(`${BASE}/orders/${orderId}/components/${componentId}/stage`, input);
}

export interface CompleteOrderInput {
  producedQtyBase: string;
  lotNo?: string | undefined;
  bestBefore?: string | null | undefined;
  cartons?: number | null | undefined;
  toBinId?: string | null | undefined;
  createPallet?: boolean | undefined;
  autoConsume?: boolean | undefined;
  note?: string | null | undefined;
}

export function completeOrder(
  id: string,
  input: CompleteOrderInput,
): Promise<CommandResult<{ status: string; lotNo: string }>> {
  return api.post(`${BASE}/orders/${id}/complete`, input);
}

export interface CreateTaskInput {
  productionOrderId?: string | null | undefined;
  kind: 'unload' | 'move_to_production' | 'general';
  title: string;
  description?: string | null | undefined;
  assignedRole?: string | null | undefined;
  assignedUser?: string | null | undefined;
  dueAt?: string | null | undefined;
}

export function createTask(input: CreateTaskInput): Promise<CommandResult<{ id: string }>> {
  return api.post(`${BASE}/tasks`, input);
}

export function updateTask(
  id: string,
  input: Partial<Omit<CreateTaskInput, 'kind' | 'productionOrderId'>> & {
    status?: 'open' | 'in_progress' | 'done';
  },
): Promise<CommandResult<{ id: string }>> {
  return api.patch(`${BASE}/tasks/${id}`, input);
}

export function completeTask(id: string): Promise<CommandResult> {
  return api.post(`${BASE}/tasks/${id}/complete`, {});
}

export interface CreateRequestInput {
  articleId: string;
  qtyBase: string;
  neededAt?: string | null | undefined;
  note?: string | null | undefined;
}

export function createRequest(input: CreateRequestInput): Promise<CommandResult<{ id: string }>> {
  return api.post(`${BASE}/requests`, input);
}

export function acceptRequest(
  id: string,
  input: { toBinId?: string | null } = {},
): Promise<CommandResult<{ taskId: string }>> {
  return api.post(`${BASE}/requests/${id}/accept`, input);
}

export function rejectRequest(id: string, note?: string): Promise<CommandResult> {
  return api.post(`${BASE}/requests/${id}/reject`, note ? { note } : {});
}

export function deliverRequest(
  id: string,
  input: { toBinId?: string | null; qtyBase?: string } = {},
): Promise<CommandResult<{ movedQtyBase: string }>> {
  return api.post(`${BASE}/requests/${id}/deliver`, input);
}
